import { Component, OnInit, OnDestroy } from '@angular/core';
import { Store } from '@ngxs/store';
import { PaymentRequestDto } from '../../model/payment-request/payment-request.dto';
import { AppDeepLinkService } from 'src/app/service/app-deeplink/app-deep-link.service';
import { SwishService } from 'src/app/service/swish/swish.service';
import {
  SwishError,
  getErrorMessage,
  SwishErrorCode,
} from 'src/app/service/swish/swish-response.dto';
import { Router } from '@angular/router';
import { routeNames } from 'src/assets/val/route-constants';
import { QueryParamsService } from 'src/app/service/query-params/query-params.service';
import { LoggingService } from 'src/app/service/logging-service/logging.service';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import {
  CountryCodes,
  formatPhoneNumber,
  phoneNumberValidator,
} from 'web-component-library/projects/component-library/src/public-api';
import { DeviceDetectorService } from 'ngx-device-detector';
import { TermsLinkService } from '@app/service/terms-link/terms-link.service';
import { RecordingService } from '@app/service/recording-service/recording.service';

@Component({
  selector: 'app-swish',
  templateUrl: './swish.component.html',
  styleUrls: ['./swish.component.css', '../page-shared.css'],
})
export class SwishComponent implements OnInit, OnDestroy {
  public phoneNumber: string;
  public isPhone: boolean;
  public error: SwishError[] = null;
  public paymentInProgress: boolean;
  public amount: number;

  token: string;
  paymentRequest: PaymentRequestDto;
  returningFromSwishApp: boolean = false;
  showError: boolean = false;
  activityTimer: any = null;
  loading: boolean = true;
  formLayout: boolean = false;
  manualDeeplinkEnabled: boolean = false;
  mobileStatusMessage: string = 'Förbereder betalning...';
  paymentStatusOtherDevice: string;
  swishId: string;
  form: FormGroup;

  constructor(
    private store: Store,
    private swishService: SwishService,
    private deepLinkService: AppDeepLinkService,
    private router: Router,
    private deviceDetectorService: DeviceDetectorService,
    private queryParamsService: QueryParamsService,
    private loggingService: LoggingService,
    private formBuilder: FormBuilder,
    private recordingService: RecordingService,
    public termsLinkService: TermsLinkService
  ) {}

  ngOnInit(): void {
    this.paymentRequest = this.store.snapshot().paymentRequest.request;
    this.amount = this.paymentRequest.amount;
    this.isPhone = this.deviceDetectorService.isMobile();
    this.swishId = this.queryParamsService.getQueryParams().swishId;
    this.phoneNumber = this.paymentRequest.phoneNumber || '';

    // If autoStart is not explicitly set to false, we will auto start
    // the payment. However, if a specific payment method is set on
    // the payment request, we will not auto start, as that flow
    // might require user interaction before starting the payment.
    const autoStart =
      this.queryParamsService.getQueryParams().autoStart !== 'false' &&
      !this.paymentRequest?.specificPaymentMethod;

    if (this.swishId) {
      this.swishService.resumePolling(this.swishId);
      this.mobileStatusMessage = 'Inväntar signering...';
      this.loading = false;

      if (this.queryParamsService.getQueryParams().poppedSwish) {
        // On returning from the swish app
        this.mobileStatusMessage = 'Slutför betalning...';
        this.returningFromSwishApp = true;
        this.manualDeeplinkEnabled = true;
      } else {
        this.setActivityTimer();
      }
    } else if (this.isPhone) {
      this.loading = false;
      this.setActivityTimer();
      this.initMobilePayment(autoStart);
    } else {
      this.formLayout = true;
      this.setupForm();
      this.loading = false;
    }
    // Some Swish flows are very fast, make sure we save the recording events.
    this.recordingService.saveRecordingEvents();
  }

  setActivityTimer(): void {
    if (!this.activityTimer) {
      this.activityTimer = setTimeout(() => {
        this.manualDeeplinkEnabled = true;
        clearTimeout(this.activityTimer);
        this.activityTimer = null;
      }, 5000);
    }
  }

  ngOnDestroy(): void {
    this.swishService.cancelCurrentSwishPayment();
    this.queryParamsService.clearQueryParam('poppedSwish');
  }

  setupForm(): void {
    this.form = this.formBuilder.group({
      phone: [
        String(this.phoneNumber) || '',
        [
          Validators.required,
          phoneNumberValidator([46], this.paymentRequest.market),
        ],
      ],
    });
  }

  public initPayment(): void {
    if (this.form.valid) {
      this.phoneNumber = formatPhoneNumber(
        this.form.value.phone.trim(),
        this.paymentRequest.market
      );

      this.paymentInProgress = true;
      this.paymentStatusOtherDevice = 'Förbereder betalning...';
      this.error = null;

      this.swishService.initSwishPayment(this.phoneNumber).subscribe(
        () => {
          this.paymentStatusOtherDevice = 'Öppna Swish-appen i din mobil.';
        },
        (err) => {
          if (err.status === 400 || err.error.status === 500) {
            this.handleFatalError();
          }
          this.paymentInProgress = false;
          this.error = err.error.errors;
        }
      );
    } else {
      this.showError = true;
    }
  }

  public initMobilePayment(autoStart: boolean): void {
    this.paymentInProgress = true;
    this.loggingService.log(
      'Initiating mobile payment, swishId=' + this.swishId
    );
    this.swishService.initSwishMobilePayment().subscribe(
      (res) => {
        this.token = res.token;
        if (autoStart) {
          this.deepLinkService.startSwish(res.token);
        } else {
          this.manualDeeplinkEnabled = true;
        }
        this.swishId = res.id;
      },
      () => this.handleFatalError()
    );
  }

  handleFatalError(): void {
    this.router.navigate([routeNames.ERROR], {
      queryParamsHandling: 'merge',
    });
  }

  restartOnOtherDevice(): void {
    this.setupForm();
    this.swishService.cancelCurrentSwishPayment();
    this.error = null;
    this.paymentStatusOtherDevice = null;
    this.paymentInProgress = false;
    this.formLayout = true;
  }

  restartMobilePayment(): void {
    this.paymentStatusOtherDevice = null;
    this.error = null;
    this.formLayout = false;
    this.mobileStatusMessage = 'Förbereder betalning...';
    this.initMobilePayment(true);
  }

  restartFormBasedPayment(): void {
    this.paymentStatusOtherDevice = null;
    this.error = null;
    this.formLayout = true;
    this.initPayment();
  }

  public getSwishErrorText(error: SwishErrorCode): string {
    return getErrorMessage(error);
  }

  public getErrorMessages(): string[] {
    return this.error.map((err: SwishError) =>
      this.getSwishErrorText(err.errorCode)
    );
  }

  isPolling(): boolean {
    return this.swishService.isPolling();
  }

  public startSwish() {
    this.deepLinkService.startSwish(this.token);
  }

  reroute(): void {
    this.router.navigate([routeNames.HOME], {
      queryParamsHandling: 'merge',
    });
  }
}
